import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaGithub, FaStar, FaCodeBranch, FaCode, FaExternalLinkAlt } from 'react-icons/fa';
import { githubService } from '../services/github';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatDistanceToNow } from 'date-fns';

const OpenSource = () => {
  const [repos, setRepos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const languageColors = {
    JavaScript: '#f1e05a',
    TypeScript: '#3178c6',
    Python: '#3572A5',
    Solidity: '#363636',
    PHP: '#4F5D95',
    Java: '#b07219',
    C: '#555555',
    'C++': '#f34b7d',
    HTML: '#e34c26',
    CSS: '#563d7c',
    Go: '#00ADD8',
    Rust: '#dea584',
    Ruby: '#701516'
  };

  useEffect(() => {
    const fetchGitHubData = async () => {
      try {
        const [reposData, statsData] = await Promise.all([
          githubService.getRepos(),
          githubService.getUserStats()
        ]);
        setRepos(reposData || []);
        setStats(statsData);
      } catch (error) {
        console.error('Error fetching GitHub data:', error);
        setRepos([
          {
            id: 1,
            name: 'HealthGuard',
            description: 'Decentralized Healthcare Identity Platform',
            stargazers_count: 0,
            forks_count: 0,
            language: 'Solidity',
            html_url: 'https://github.com/developer13-art/HealthGuard',
            updated_at: new Date().toISOString()
          },
          {
            id: 2,
            name: 'FYP-Assist-Hub',
            description: 'Digital marketplace for academic projects',
            stargazers_count: 0,
            forks_count: 0,
            language: 'JavaScript',
            html_url: '#',
            updated_at: new Date().toISOString()
          }
        ]);
        setStats({
          repos: 2,
          stars: 0,
          followers: 0,
          languages: ['Solidity', 'JavaScript', 'TypeScript']
        });
      } finally {
        setLoading(false);
      }
    };
    fetchGitHubData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Open Source
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            <span className="gradient-text">Open Source</span> Projects
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            My contributions to the open source community
          </p>
        </motion.div>

        {/* GitHub Stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{stats.repos}</div>
              <div className="text-sm text-gray-400">Repositories</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{stats.stars}</div>
              <div className="text-sm text-gray-400">Stars</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{stats.followers}</div>
              <div className="text-sm text-gray-400">Followers</div>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{stats.languages?.length || 0}</div>
              <div className="text-sm text-gray-400">Languages</div>
            </div>
          </motion.div>
        )}

        {/* Languages */}
        {stats?.languages && stats.languages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex flex-wrap gap-2 mb-8 justify-center"
          >
            {stats.languages.map((lang, i) => (
              <span 
                key={i}
                className="px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm"
              >
                {lang}
              </span>
            ))}
          </motion.div>
        )}

        {/* Repositories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {repos.map((repo, index) => (
            <motion.div
              key={repo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <a 
                    href={repo.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-semibold text-white hover:text-primary-400 transition-colors duration-300 flex items-center gap-2"
                  >
                    {repo.name}
                    <FaExternalLinkAlt size={14} className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </a>
                  
                  {repo.description && (
                    <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                      {repo.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                {repo.language && (
                  <span className="flex items-center gap-1.5 text-gray-300">
                    <span 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: languageColors[repo.language] || '#6b6b6b' }}
                    />
                    {repo.language}
                  </span>
                )}
                
                {repo.stargazers_count > 0 && (
                  <span className="flex items-center gap-1 text-gray-400">
                    <FaStar className="text-yellow-500" />
                    {repo.stargazers_count}
                  </span>
                )}
                
                {repo.forks_count > 0 && (
                  <span className="flex items-center gap-1 text-gray-400">
                    <FaCodeBranch />
                    {repo.forks_count}
                  </span>
                )}
                
                {repo.updated_at && (
                  <span className="text-gray-500 text-xs">
                    Updated {formatDistanceToNow(new Date(repo.updated_at), { addSuffix: true })}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {repos.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No repositories found.</p>
          </div>
        )}

        {/* GitHub Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-10"
        >
          <a 
            href="https://github.com/developer13-art" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 border border-gray-700 rounded-lg text-gray-300 hover:bg-white/5 transition-all duration-300"
          >
            <FaGithub />
            View All Projects on GitHub
            <FaExternalLinkAlt size={14} />
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default OpenSource;